import { useState, useEffect, useContext } from "react";
import { Link } from "react-router-dom";
import { Crown, ArrowRight, ChevronRight, Sparkles } from "lucide-react";
import { Button } from "../components/ui/button";
import GirlCard from "../components/GirlCard";
import NewsletterSignup from "../components/NewsletterSignup";
import SEOHead from "../components/SEOHead";
import useAnalytics from "../hooks/useAnalytics";
import { AppContext } from "../App";
import { useLanguage } from "../contexts/LanguageContext";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const HomePage = () => {
  const { settings, favorites } = useContext(AppContext);
  const { language, t } = useLanguage();
  const { trackPageView, trackButtonClick } = useAnalytics();
  const [girls, setGirls] = useState([]);
  const [galleryImages, setGalleryImages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    trackPageView("/home");
    const fetchData = async () => {
      try {
        const [girlsRes, galleryRes] = await Promise.all([
          axios.get(`${API}/girls/preview?limit=8`),
          axios.get(`${API}/gallery?landing_only=true&limit=6`),
        ]);
        setGirls(girlsRes.data);
        setGalleryImages(galleryRes.data.images || []);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const heroImage = settings?.hero_image || "https://images.unsplash.com/photo-1614535428216-41a2e395ef88?w=1920";

  return (
    <div data-testid="home-page">
      <SEOHead 
        title={null}
        description="Diskretion, Eleganz und unvergessliche Momente."
        image={heroImage}
        url="/"
      />
      {/* Hero Section */}
      <section className="relative h-screen min-h-[600px] flex items-center" data-testid="hero-section">
        {/* Background */}
        <div className="absolute inset-0">
          <img
            src={heroImage}
            alt="Monaco Bar"
            className="w-full h-full object-cover"
            fetchpriority="high"
          />
          <div 
            className="absolute inset-0"
            style={{
              background: "linear-gradient(90deg, rgba(7,7,10,0.95) 0%, rgba(7,7,10,0.7) 50%, rgba(7,7,10,0.4) 100%)"
            }}
          />
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 w-full">
          <div className="max-w-2xl">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-monaco-red/10 border border-monaco-red/30 mb-6 fade-in">
              <Sparkles className="w-4 h-4 text-monaco-red" />
              <span className="text-sm text-monaco-red font-medium">
                {language === "de" ? "Exklusiv in Wien" : "Exclusive in Vienna"}
              </span>
            </div>

            {/* Title */}
            <h1 className="font-display text-5xl md:text-7xl text-monaco-text-0 mb-6 italic fade-in stagger-1">
              {t(settings, "hero_title", "Monaco Bar")}
            </h1>

            {/* Subtitle */}
            <p className="text-xl md:text-2xl text-monaco-text-1 mb-8 leading-relaxed fade-in stagger-2">
              {t(settings, "hero_subtitle", language === "de" 
                ? "Willkommen in der Monaco Bar! Entspannte Atmosphäre. Unvergessliche Abende."
                : "Welcome to Monaco Bar! Relaxed atmosphere. Unforgettable evenings."
              )}
            </p>

            {/* USPs */}
            <div className="flex flex-wrap gap-4 mb-10 fade-in stagger-3">
              {(language === "de" 
                ? ["VIP Service", "100% Diskret", "Zentrale Lage"]
                : ["VIP Service", "100% Discrete", "Central Location"]
              ).map((usp, i) => (
                <span 
                  key={i}
                  className="px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-monaco-text-1"
                >
                  {usp}
                </span>
              ))}
            </div>

            {/* CTAs */}
            <div className="flex flex-wrap gap-4 fade-in stagger-4">
              <Link to="/girls">
                <Button 
                  variant="outline" 
                  size="lg"
                  className="h-14 px-8 border-white/20 text-monaco-text-0 hover:bg-white/10 text-lg"
                  data-testid="hero-girls-btn"
                >
                  {language === "de" ? "Girls ansehen" : "View Girls"}
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              {settings?.booking_button_enabled !== false && (
                <Link to="/buchen">
                  <Button 
                    size="lg"
                    className="h-14 px-8 bg-monaco-red hover:bg-monaco-red-hover text-white text-lg btn-shine relative overflow-hidden neon-glow"
                    data-testid="hero-book-btn"
                  >
                    <Crown className="w-5 h-5 mr-2" />
                    {language === "de" ? "Jetzt buchen" : "Book Now"}
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 rounded-full border-2 border-white/20 flex justify-center pt-2">
            <div className="w-1.5 h-3 bg-monaco-red rounded-full" />
          </div>
        </div>
      </section>

      {/* Girls Preview Section */}
      <section className="py-24 md:py-32 bg-monaco-bg-0" data-testid="girls-preview-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          {/* Section Header */}
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12">
            <div>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-monaco-red mb-3 block">
                {t(settings, "homepage_section_title", language === "de" ? "Unsere Girls" : "Our Girls")}
              </span>
              <h2 className="font-display text-4xl md:text-5xl text-monaco-text-0">
                {t(settings, "homepage_section_subtitle", language === "de" ? "Exklusive Frauen" : "Exclusive Ladies")}
              </h2>
            </div>
            {(settings?.girls_display_mode === "internal" || (!settings?.girls_display_mode && settings?.girls_section_enabled !== false)) && (
              <Link 
                to="/girls"
                className="mt-4 md:mt-0 inline-flex items-center text-monaco-red hover:text-monaco-red-hover transition-colors"
                data-testid="view-all-girls"
              >
                {language === "de" ? "Alle ansehen" : "View all"}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            )}
          </div>

          {/* Girls Grid / External Redirect / Visit Us */}
          {settings?.girls_display_mode === "visit" ? (
            // Visit Us Mode
            <div className="relative rounded-2xl overflow-hidden border border-white/10 bg-monaco-bg-1">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
                {/* Image */}
                <div className="relative aspect-[16/9] lg:aspect-[4/3]">
                  <img
                    src={settings?.girls_visit_image || "https://images.unsplash.com/photo-1614535428216-41a2e395ef88?w=1200"}
                    alt={t(settings, "girls_visit_title", language === "de" ? "Unsere Girls" : "Our Girls")}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t lg:bg-gradient-to-r from-monaco-bg-1 via-monaco-bg-1/50 to-transparent" />
                </div>
                
                {/* Content */}
                <div className="p-8 lg:p-12 flex flex-col justify-center">
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-monaco-red/10 border border-monaco-red/30 mb-4 w-fit">
                    <Crown className="w-3.5 h-3.5 text-monaco-red" />
                    <span className="text-xs text-monaco-red font-medium">{language === "de" ? "Persönlich erleben" : "Experience in Person"}</span>
                  </div>
                  
                  <h3 className="font-display text-2xl lg:text-3xl text-monaco-text-0 mb-3 italic">
                    {t(settings, "girls_visit_title", language === "de" ? "Unsere Girls" : "Our Girls")}
                  </h3>
                  
                  <p className="text-monaco-text-1 mb-6 leading-relaxed">
                    {t(settings, "girls_visit_description", language === "de" 
                      ? "Um unsere aktuellen Girls kennenzulernen, besuche uns persönlich in der Monaco Bar. Wir freuen uns auf dich!"
                      : "To meet our current girls, visit us in person at Monaco Bar. We look forward to seeing you!"
                    )}
                  </p>
                  
                  <Link to="/contact" className="inline-block w-fit">
                    <Button 
                      className="h-12 px-8 bg-monaco-red hover:bg-monaco-red-hover text-white btn-shine relative overflow-hidden neon-glow group"
                    >
                      {language === "de" ? "Kontakt aufnehmen" : "Get in Touch"}
                      <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          ) : settings?.girls_display_mode === "external" || settings?.girls_section_enabled === false ? (
            // External Redirect Mode
            <div className="relative rounded-2xl overflow-hidden border border-white/10 bg-monaco-bg-1">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
                {/* Image */}
                <div className="relative aspect-[16/9] lg:aspect-[4/3]">
                  <img
                    src={settings?.girls_external_image || "https://images.unsplash.com/photo-1614535428216-41a2e395ef88?w=1200"}
                    alt={settings?.girls_external_title || "Unsere Girls"}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t lg:bg-gradient-to-r from-monaco-bg-1 via-monaco-bg-1/50 to-transparent" />
                </div>
                
                {/* Content */}
                <div className="p-8 lg:p-12 flex flex-col justify-center">
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-monaco-red/10 border border-monaco-red/30 mb-4 w-fit">
                    <Crown className="w-3.5 h-3.5 text-monaco-red" />
                    <span className="text-xs text-monaco-red font-medium">{language === "de" ? "Über unseren Partner" : "Via our Partner"}</span>
                  </div>
                  
                  <h3 className="font-display text-2xl lg:text-3xl text-monaco-text-0 mb-3 italic">
                    {settings?.girls_external_title || "Our Girls"}
                  </h3>
                  
                  <p className="text-monaco-text-1 mb-6">
                    {settings?.girls_external_description || "Discover our exclusive ladies on our partner site."}
                  </p>
                  
                  <a 
                    href={settings?.girls_external_link || "#"}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block w-fit"
                  >
                    <Button 
                      className="h-12 px-8 bg-monaco-red hover:bg-monaco-red-hover text-white btn-shine relative overflow-hidden neon-glow group"
                    >
                      {settings?.girls_external_button_text || "Discover Now"}
                      <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          ) : loading ? (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="aspect-[3/4] bg-monaco-bg-1 rounded-xl animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
              {girls.map((girl) => (
                <GirlCard
                  key={girl.id}
                  girl={girl}
                  isFavorite={favorites.isFavorite(girl.id)}
                  onToggleFavorite={favorites.toggleFavorite}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 md:py-32 bg-monaco-bg-1">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: "Bar",
                description: language === "de" ? "Entspannen Sie sich an unserer exklusiven Bar in stilvoller Atmosphäre." : "Relax at our exclusive bar in a stylish atmosphere.",
                link: "/bar",
                icon: "🍸"
              },
              {
                title: "Separee",
                description: language === "de" ? "Private Räume für ungestörte, intime Momente." : "Private rooms for undisturbed, intimate moments.",
                link: "/separee",
                icon: "🛋️"
              },
              {
                title: "VIP Service",
                description: language === "de" ? "Persönliche Aufmerksamkeit und individuelle Wünsche." : "Personal attention and individual wishes.",
                link: "/contact",
                icon: "👑"
              }
            ].map((feature, i) => (
              <Link 
                key={i} 
                to={feature.link}
                className="group p-8 rounded-xl bg-monaco-bg-2 border border-white/5 hover:border-monaco-red/30 transition-all card-hover"
              >
                <span className="text-4xl mb-4 block">{feature.icon}</span>
                <h3 className="font-display text-2xl text-monaco-text-0 mb-3 group-hover:text-monaco-red transition-colors">
                  {feature.title}
                </h3>
                <p className="text-monaco-text-1 mb-4">
                  {feature.description}
                </p>
                <span className="inline-flex items-center text-sm text-monaco-red">
                  {language === "de" ? "Mehr erfahren" : "Learn more"}
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Preview */}
      {galleryImages.length > 0 && (
        <section className="py-24 md:py-32 bg-monaco-bg-0" data-testid="gallery-preview-section">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="flex flex-col md:flex-row md:items-end justify-between mb-12">
              <div>
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-monaco-red mb-3 block">
                  {language === "de" ? "Impressionen" : "Impressions"}
                </span>
                <h2 className="font-display text-4xl md:text-5xl text-monaco-text-0">
                  {language === "de" ? "Galerie" : "Gallery"}
                </h2>
              </div>
              <Link 
                to="/gallery" 
                className="mt-4 md:mt-0 inline-flex items-center text-monaco-red hover:text-monaco-red-hover transition-colors"
              >
                {language === "de" ? "Mehr ansehen" : "View more"}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {galleryImages.slice(0, 6).map((img, i) => (
                <div 
                  key={img.id} 
                  className={`relative overflow-hidden rounded-xl img-zoom ${
                    i === 0 ? "col-span-2 row-span-2" : ""
                  }`}
                >
                  <img
                    src={img.url}
                    alt={img.title || "Gallery"}
                    className={`w-full object-cover ${i === 0 ? "h-full min-h-[400px]" : "aspect-square"}`}
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-monaco-bg-0/60 to-transparent opacity-0 hover:opacity-100 transition-opacity" />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Polterabend Teaser */}
      <section className="py-24 md:py-32 bg-gradient-to-b from-monaco-bg-0 to-monaco-bg-1" data-testid="polterabend-teaser">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-monaco-red mb-3 block">
            {language === "de" ? "Feiern mit Stil" : "Celebrate in Style"}
          </span>
          <h2 className="font-display text-4xl md:text-5xl text-monaco-text-0 mb-4">
            {language === "de" ? "Polterabend" : "Bachelor Party"}
          </h2>
          <p className="text-xl text-monaco-text-1 mb-8">
            {language === "de" 
              ? "Feiere deinen Polterabend bei uns – 5 exklusive Pakete für eine unvergessliche Nacht!"
              : "Celebrate your bachelor party with us – 5 exclusive packages for an unforgettable night!"}
          </p>
          <Link to="/polterabend">
            <Button 
              size="lg"
              className="h-14 px-10 bg-monaco-red hover:bg-monaco-red-hover text-white text-lg btn-shine relative overflow-hidden neon-glow"
              data-testid="polterabend-cta-btn"
            >
              <Crown className="w-5 h-5 mr-2" />
              {language === "de" ? "Zu den Paketen" : "View Packages"}
            </Button>
          </Link>
        </div>
      </section>

      {/* CTA Section */}
      {settings?.booking_button_enabled !== false && (
        <section className="py-24 md:py-32 bg-gradient-to-b from-monaco-bg-0 to-monaco-bg-1" data-testid="cta-section">
          <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
            <h2 className="font-display text-4xl md:text-5xl text-monaco-text-0 mb-4">
              {t(settings, "homepage_cta_title", language === "de" ? "Bereit für ein unvergessliches Erlebnis?" : "Ready for an unforgettable experience?")}
            </h2>
            <p className="text-xl text-monaco-text-1 mb-8">
              {t(settings, "homepage_cta_subtitle", language === "de" ? "Buche jetzt deinen exklusiven Moment" : "Book your exclusive moment now")}
            </p>
            <Link to="/buchen">
              <Button 
                size="lg"
                className="h-14 px-10 bg-monaco-red hover:bg-monaco-red-hover text-white text-lg btn-shine relative overflow-hidden neon-glow"
                data-testid="cta-book-btn"
              >
                <Crown className="w-5 h-5 mr-2" />
                {language === "de" ? "Jetzt Buchen" : "Book Now"}
              </Button>
            </Link>
          </div>
        </section>
      )}

      {/* Newsletter Section */}
      <section className="py-24 md:py-32 bg-monaco-bg-1" data-testid="newsletter-section">
        <div className="max-w-3xl mx-auto px-4 md:px-8 text-center">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-monaco-red mb-3 block">
            {language === "de" ? "Immer informiert" : "Stay Informed"}
          </span>
          <h2 className="font-display text-4xl md:text-5xl text-monaco-text-0 mb-4">
            Newsletter
          </h2>
          <p className="text-monaco-text-1 mb-8 max-w-xl mx-auto">
            {language === "de" 
              ? "Erhalte 2x pro Woche exklusive Updates über unsere neuesten Girls und Angebote."
              : "Receive exclusive updates about our latest girls and offers twice a week."}
          </p>
          <NewsletterSignup />
        </div>
      </section>
    </div>
  );
};

export default HomePage;
